"use client";

import { useId, useMemo, useState } from "react";
import { useFormStatus } from "react-dom";
import { useLocale, useTranslations } from "next-intl";
import { defaultLocale, formatPrice, isValidLocale } from "@/config/locale";
import { isPurchasableVariant, sortVariantsByStrengthAndPackage } from "@/lib/packaging/variant-order";
import { getVariantDisplayLabel } from "@/lib/packaging/variant-display-label";
import { AddToCartForm, useAddToCartFeedback } from "@/ui/components/pdp/add-to-cart-form";
import { PosthogDistinctIdField } from "@/ui/components/pdp/posthog-distinct-id-field";
import type { ProductCardVariant } from "./product-card";
import { ProductCardPrice, type ProductCardPriceData } from "./product-card-price";

interface ProductCardCartProps {
	channel?: string;
	locale?: string;
	productName: string;
	variants: ProductCardVariant[];
	pricing?: ProductCardPriceData;
}

function isAvailable(variant: ProductCardVariant) {
	return isPurchasableVariant({
		quantityAvailable: variant.quantityAvailable,
		pricing:
			variant.price != null && variant.currency
				? { price: { gross: { amount: variant.price, currency: variant.currency } } }
				: null,
	});
}

function formatVariantPrice(variant: ProductCardVariant, locale: string | undefined) {
	if (variant.price == null || !variant.currency) return null;
	return formatPrice(
		variant.price,
		variant.currency,
		locale && isValidLocale(locale) ? locale : defaultLocale,
	);
}

function VariantPicker({
	id,
	variants,
	variantId,
	setVariantId,
	locale,
}: {
	id: string;
	variants: ProductCardVariant[];
	variantId: string;
	setVariantId: (id: string) => void;
	locale?: string;
}) {
	const { pending } = useFormStatus();
	const t = useTranslations("product");
	const tKitUnit = useTranslations("kitWebsite.unit");
	const activeLocale = useLocale();
	const labelLocale = locale && isValidLocale(locale) ? locale : activeLocale;

	return (
		<>
			<label className="sr-only" htmlFor={id}>
				{t("chooseVariant")}
			</label>
			<select
				id={id}
				value={variantId}
				disabled={pending}
				onChange={(event) => setVariantId(event.target.value)}
				className="h-10 w-full rounded-md border border-border bg-background px-2 text-xs disabled:cursor-wait disabled:opacity-70"
			>
				{!variantId && (
					<option value="" disabled>
						{t("outOfStock")}
					</option>
				)}
				{variants.map((variant) => {
					const price = formatVariantPrice(variant, locale);
					const available = isAvailable(variant);
					return (
						<option key={variant.id} value={variant.id} disabled={!available}>
							{getVariantDisplayLabel(variant.name, labelLocale, (values) => tKitUnit("summary", values))}
							{price ? ` · ${price}` : ""}
							{available ? "" : ` · ${t("outOfStock")}`}
						</option>
					);
				})}
			</select>
		</>
	);
}

function SubmitButton({ disabled }: { disabled: boolean }) {
	const { pending } = useFormStatus();
	const feedback = useAddToCartFeedback();
	const t = useTranslations("product");
	const success = feedback?.success === true;

	return (
		<button
			type="submit"
			disabled={disabled || pending}
			className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-full bg-primary-hero px-3 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary disabled:cursor-not-allowed disabled:opacity-70 sm:h-14 sm:text-[0.75rem] sm:tracking-[0.2em]"
		>
			{success ? t("addedToBag") : pending ? t("adding") : disabled ? t("outOfStock") : t("addToBag")}
		</button>
	);
}

export function ProductCardCart({ channel, locale, productName, variants, pricing }: ProductCardCartProps) {
	// The server action rejects an invalid locale, and cards rendered without
	// price enrichment carry none — fall back to the active UI locale.
	const activeLocale = useLocale();
	const formLocale = locale && isValidLocale(locale) ? locale : activeLocale;
	const orderedVariants = useMemo(() => sortVariantsByStrengthAndPackage(variants), [variants]);
	const [chosenVariantId, setVariantId] = useState<string>();
	const variantId =
		orderedVariants.find((variant) => variant.id === chosenVariantId)?.id ??
		orderedVariants.find(isAvailable)?.id ??
		"";
	const selectId = useId();
	const selectedVariant = variants.find((variant) => variant.id === variantId) ?? null;
	const selectedPricing =
		selectedVariant && pricing
			? {
					...pricing,
					price: selectedVariant.price ?? pricing.price,
					currency: selectedVariant.currency ?? pricing.currency,
					pricePresentation: undefined,
					compareAtPrice: null,
					compareAtPresentation: null,
					perVialPresentation: undefined,
				}
			: pricing;
	const canAdd = Boolean(channel && selectedVariant && isAvailable(selectedVariant));
	const hasVariantChoice = variants.length > 1;

	return (
		<AddToCartForm>
			<input type="hidden" name="channel" value={channel ?? ""} />
			<input type="hidden" name="locale" value={formLocale} />
			<input type="hidden" name="variantId" value={selectedVariant?.id ?? ""} />
			<input type="hidden" name="quantity" value="1" />
			<input type="hidden" name="productName" value={productName} />
			<input type="hidden" name="variantName" value={selectedVariant?.name ?? ""} />
			<PosthogDistinctIdField />
			<div className="flex flex-col gap-2">
				{selectedPricing ? <ProductCardPrice product={selectedPricing} /> : null}
				{hasVariantChoice ? (
					<VariantPicker
						id={`card-variant-${selectId}`}
						variants={orderedVariants}
						variantId={variantId}
						setVariantId={setVariantId}
						locale={locale}
					/>
				) : null}
				<SubmitButton disabled={!canAdd} />
			</div>
		</AddToCartForm>
	);
}
